import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../context/AuthContext';

export type PresenceStatus = 'online' | 'busy' | 'offline';

interface UserPresence {
  user_id: string;
  status: PresenceStatus;
  last_seen: string;
  firstName?: string;
  lastName?: string;
}

export function usePresence(userId?: string) {
  const { user } = useAuthContext();
  const [status, setStatus] = useState<PresenceStatus>('offline');
  const [presenceMap, setPresenceMap] = useState<Record<string, UserPresence>>({});
  const channelRef = useRef<any>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout>();
  const mounted = useRef(true);

  // Initialize presence tracking
  useEffect(() => {
    mounted.current = true;

    const initPresence = async () => {
      if (!user?.id) return;

      try {
        // Clean up any existing channel
        if (channelRef.current) {
          await channelRef.current.unsubscribe();
        }

        // Get user profile data
        const { data: profile } = await supabase
          .from('profiles')
          .select('firstName, lastName')
          .eq('id', user.id)
          .single();

        // Create new presence channel
        const channel = supabase.channel('online_users');
        channelRef.current = channel;

        // Set up presence handlers
        channel
          .on('presence', { event: 'sync' }, () => {
            if (!mounted.current) return;
            const state = channel.presenceState();
            const now = Date.now();
            const newPresenceMap: Record<string, UserPresence> = {};
            
            Object.values(state).forEach(presences => {
              if (Array.isArray(presences) && presences.length > 0) {
                const presence = presences[0] as UserPresence;
                const lastSeen = new Date(presence.last_seen).getTime();
                
                // Only include users seen within last 30 seconds
                if (now - lastSeen <= 30000) {
                  newPresenceMap[presence.user_id] = presence;
                }
              }
            });
            
            setPresenceMap(newPresenceMap);
          })
          .on('presence', { event: 'join' }, ({ newPresence }: any) => {
            if (!mounted.current || !Array.isArray(newPresence) || !newPresence.length) return;
            const presence = newPresence[0] as UserPresence;
            setPresenceMap(prev => ({ ...prev, [presence.user_id]: presence }));
          })
          .on('presence', { event: 'leave' }, ({ leftPresence }: any) => {
            if (!mounted.current || !Array.isArray(leftPresence) || !leftPresence.length) return;
            const presence = leftPresence[0] as UserPresence;
            setPresenceMap(prev => {
              const newMap = { ...prev };
              delete newMap[presence.user_id];
              return newMap;
            });
          });

        // Subscribe and track initial presence
        await channel.subscribe(async (status) => {
          if (status === 'SUBSCRIBED' && mounted.current) {
            await channel.track({
              user_id: user.id,
              status: 'online',
              last_seen: new Date().toISOString(),
              firstName: profile?.firstName,
              lastName: profile?.lastName
            });
          }
        });

        // Set up heartbeat to maintain presence
        heartbeatIntervalRef.current = setInterval(async () => {
          if (!mounted.current) return;
          try {
            await channel.track({
              user_id: user.id,
              status: status === 'busy' ? 'busy' : 'online',
              last_seen: new Date().toISOString(),
              firstName: profile?.firstName,
              lastName: profile?.lastName
            });
          } catch (error) {
            console.error('Heartbeat error:', error);
          }
        }, 15000); // Send heartbeat every 15 seconds
      } catch (error) {
        console.error('Presence error:', error);
        if (mounted.current) {
          setStatus('offline');
        }
      }
    };

    initPresence();

    // Cleanup function
    return () => {
      mounted.current = false;
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (channelRef.current) {
        channelRef.current.unsubscribe();
      }
    };
  }, [user]);

  // Update status handler
  const updateStatus = useCallback(async (newStatus: PresenceStatus) => {
    if (!user?.id || !channelRef.current) return;
    
    try {
      // Get current profile data
      const { data: profile } = await supabase
        .from('profiles')
        .select('firstName, lastName')
        .eq('id', user.id)
        .single();

      await channelRef.current.track({
        user_id: user.id,
        status: newStatus,
        last_seen: new Date().toISOString(),
        firstName: profile?.firstName,
        lastName: profile?.lastName
      });
      setStatus(newStatus);
    } catch (error) {
      console.error('Status update error:', error);
    }
  }, [user]);

  // Get user status with caching
  const getUserStatus = useCallback((targetUserId?: string): PresenceStatus => {
    if (!targetUserId) return status;
    
    const presence = presenceMap[targetUserId];
    if (!presence) return 'offline';

    const lastSeen = new Date(presence.last_seen).getTime();
    if (Date.now() - lastSeen > 30000) {
      return 'offline';
    }

    return presence.status;
  }, [presenceMap, status]);

  // Get user name from presence data
  const getUserName = useCallback((targetUserId?: string): string | null => {
    if (!targetUserId) return null;
    
    const presence = presenceMap[targetUserId];
    if (!presence || !presence.firstName) return null;

    return presence.lastName 
      ? `${presence.firstName} ${presence.lastName.charAt(0)}.`
      : presence.firstName;
  }, [presenceMap]);

  return {
    status: getUserStatus(userId),
    updateStatus,
    getUserName
  };
}